import jsPDF from 'jspdf'
import { formatDate } from '../utils/helpers'

export const PDF_SECTION_OPTIONS = [
  { id: 'personal', label: 'Personal Information' },
  { id: 'medical', label: 'Medical History' },
  { id: 'lifestyle', label: 'Lifestyle & Wellness' },
  { id: 'goals', label: 'Health Goals' },
  { id: 'vitals', label: 'Vital Signs' },
  { id: 'questionnaire', label: 'Questionnaire & Charting' },
  { id: 'consent', label: 'Consent & Signatures' },
]

export const DEFAULT_PDF_SECTIONS = PDF_SECTION_OPTIONS.map((s) => s.id)

const formatHabitValue = (value) => {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item
        if (item?.text) return item.tagged ? `${item.text} [tagged]` : item.text
        return ''
      })
      .filter(Boolean)
      .join('; ')
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, habits]) => {
        const text = formatHabitValue(habits)
        return text ? `${key}: ${text}` : ''
      })
      .filter(Boolean)
      .join(' | ')
  }
  return String(value)
}

/**
 * Build examination PDF. Supports section selection and edited questions.
 */
const buildExaminationPdf = ({
  examination = {},
  client = {},
  medicalHistory = {},
  options = {},
}) => {
  const {
    blankQuestionnaireSlots = 4,
    title = 'NaturAssist Examination Record',
    sections = DEFAULT_PDF_SECTIONS,
  } = options

  const include = (id) => sections.includes(id)
  let sectionNumber = 0
  const nextSectionLabel = (label) => {
    sectionNumber += 1
    return `${sectionNumber}. ${label}`
  }

  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 16
  const contentWidth = pageWidth - 2 * margin
  const yPos = { value: margin }

  const checkPageBreak = (requiredSpace = 24) => {
    if (yPos.value + requiredSpace > pageHeight - margin - 8) {
      doc.addPage()
      yPos.value = margin
      return true
    }
    return false
  }

  const sectionTitle = (titleText) => {
    checkPageBreak(22)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(5, 150, 105)
    doc.text(titleText, margin, yPos.value)
    yPos.value += 2.5
    doc.setDrawColor(16, 185, 129)
    doc.setLineWidth(0.4)
    doc.line(margin, yPos.value, pageWidth - margin, yPos.value)
    yPos.value += 7
    doc.setTextColor(0, 0, 0)
    doc.setLineWidth(0.2)
  }

  const drawWriteLines = (lineCount = 2) => {
    doc.setDrawColor(180)
    for (let i = 0; i < lineCount; i++) {
      checkPageBreak(8)
      yPos.value += 5
      doc.line(margin + 2, yPos.value, pageWidth - margin, yPos.value)
    }
    yPos.value += 4
    doc.setDrawColor(0)
  }

  const fieldRow = (label, value, { lines = 1, inline = false } = {}) => {
    const text = formatHabitValue(value)
    checkPageBreak(inline ? 10 : 10 + lines * 6)

    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(55)
    doc.text(label, margin, yPos.value)
    doc.setTextColor(0, 0, 0)

    if (inline) {
      const labelWidth = doc.getTextWidth(label) + 3
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      if (text) {
        doc.text(text, margin + labelWidth, yPos.value)
      } else {
        doc.setDrawColor(180)
        doc.line(margin + labelWidth, yPos.value, pageWidth - margin, yPos.value)
        doc.setDrawColor(0)
      }
      yPos.value += 7
      return
    }

    yPos.value += 4
    if (text) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      const wrapped = doc.splitTextToSize(text, contentWidth - 2)
      wrapped.forEach((line) => {
        checkPageBreak(6)
        doc.text(line, margin + 2, yPos.value)
        yPos.value += 5
      })
      if (lines > 1) {
        drawWriteLines(Math.max(1, lines - wrapped.length))
      } else {
        yPos.value += 3
      }
    } else {
      drawWriteLines(lines)
    }
  }

  // Header
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(5, 150, 105)
  doc.text(title, margin, yPos.value)
  yPos.value += 8
  doc.setTextColor(0, 0, 0)

  // Leave blank for handwriting / paper fill-in
  fieldRow('Examination date:', '', { inline: true })
  yPos.value += 2

  if (include('personal')) {
    sectionTitle(nextSectionLabel('Personal Information'))
    fieldRow('First name:', client.first_name || '', { inline: true })
    fieldRow('Last name:', client.last_name || '', { inline: true })
    fieldRow('Date of birth:', client.date_of_birth || '', { inline: true })
    fieldRow('Gender:', client.gender || '', { inline: true })
    fieldRow('Phone:', client.phone || '', { inline: true })
    fieldRow('Email:', client.email || '', { inline: true })
  }

  if (include('medical')) {
    sectionTitle(nextSectionLabel('Medical History'))
    fieldRow('Current medications', medicalHistory.current_medications, { lines: 3 })
    fieldRow('Allergies', medicalHistory.allergies, { lines: 2 })
    fieldRow('Past surgeries', medicalHistory.past_surgeries, { lines: 2 })
    fieldRow('Family medical history', medicalHistory.family_history, { lines: 2 })
    fieldRow('Chronic conditions', medicalHistory.chronic_conditions, { lines: 2 })
  }

  if (include('lifestyle')) {
    sectionTitle(nextSectionLabel('Lifestyle & Wellness'))
    fieldRow('Diet / nutrition habits', medicalHistory.diet, { lines: 2 })
    fieldRow('Exercise habits', medicalHistory.exercise, { lines: 2 })
    fieldRow('Sleep habits', medicalHistory.sleep, { lines: 2 })
    fieldRow('Custom habits / other', medicalHistory.custom_habits, { lines: 2 })
    fieldRow('Stress level (Low / Moderate / High / Very High):', medicalHistory.stress_level || '', { inline: true })
    fieldRow('Alcohol (None / Occasional / Regular / Heavy):', medicalHistory.alcohol || '', { inline: true })
    fieldRow('Smoking (Non-smoker / Former / Current):', medicalHistory.smoking || '', { inline: true })
  }

  if (include('goals')) {
    sectionTitle(nextSectionLabel('Health Goals'))
    fieldRow('Primary health goals', medicalHistory.primary_goals, { lines: 3 })
    fieldRow('Treatment expectations', medicalHistory.expectations, { lines: 2 })
    fieldRow('Previous naturopathic treatments', medicalHistory.previous_treatments, { lines: 2 })
  }

  if (include('vitals')) {
    sectionTitle(nextSectionLabel('Vital Signs'))
    const vitals = examination.vital_signs || {}
    fieldRow('Blood pressure (mmHg):', vitals.bloodPressure || '', { inline: true })
    fieldRow('Heart rate (bpm):', vitals.heartRate || '', { inline: true })
    fieldRow('Respiratory rate (breaths/min):', vitals.respiratoryRate || '', { inline: true })
    fieldRow('Temperature (°C):', vitals.temperature || '', { inline: true })
    fieldRow('SpO₂ (%):', vitals.oxygenSaturation || '', { inline: true })
    fieldRow('Weight (kg):', vitals.weight || '', { inline: true })
    fieldRow('Height (cm):', vitals.height || '', { inline: true })
    fieldRow('BMI:', vitals.bmi || '', { inline: true })
  }

  if (include('questionnaire')) {
    sectionTitle(nextSectionLabel('Questionnaire & Charting'))
    const questions = Array.isArray(examination.custom_questions)
      ? examination.custom_questions.filter((q) => q && (q.prompt || q.answer))
      : []

    if (questions.length > 0) {
      questions.forEach((question, index) => {
        checkPageBreak(28)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        doc.text(`Q${index + 1}. ${question.prompt || 'Question'}`, margin, yPos.value)
        yPos.value += 4
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.setTextColor(110)
        doc.text(
          question.type === 'choice' ? 'Response: Yes / No / Maybe / Not Known / Other' : 'Open response',
          margin + 2,
          yPos.value
        )
        doc.setTextColor(0, 0, 0)
        yPos.value += 4
        const answer = formatHabitValue(question.answer)
        if (answer) {
          doc.setFontSize(10)
          const wrapped = doc.splitTextToSize(answer, contentWidth - 2)
          wrapped.forEach((line) => {
            checkPageBreak(6)
            doc.text(line, margin + 2, yPos.value)
            yPos.value += 5
          })
          yPos.value += 2
        } else {
          drawWriteLines(question.type === 'choice' ? 1 : 3)
        }
      })
    }

    const emptySlots = Math.max(0, Number(blankQuestionnaireSlots) || 0)
    for (let i = 0; i < emptySlots; i++) {
      checkPageBreak(28)
      const n = questions.length + i + 1
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.text(`Q${n}. Question / charting note:`, margin, yPos.value)
      yPos.value += 1
      drawWriteLines(1)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(110)
      doc.text('Answer / findings:', margin + 2, yPos.value)
      doc.setTextColor(0, 0, 0)
      yPos.value += 1
      drawWriteLines(2)
    }
  }

  if (include('consent')) {
    sectionTitle(nextSectionLabel('Consent & Signatures'))
    fieldRow('Client consent obtained (Yes / No):', examination.client_consent || '', { inline: true })
    fieldRow('Consent date:', examination.consent_date || '', { inline: true })
    fieldRow('Witness / practitioner:', examination.consent_witness || '', { inline: true })
    yPos.value += 2
    fieldRow('Client signature', '', { lines: 2 })
    fieldRow('Practitioner signature', '', { lines: 2 })
    fieldRow('Additional clinical notes', examination.notes || '', { lines: 4 })
  }

  // Page numbers only
  const totalPages = doc.internal.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120)
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' })
    doc.setTextColor(0, 0, 0)
  }

  return doc
}

const saveDoc = (doc, client) => {
  const safeName = `${client?.last_name || 'client'}-${client?.first_name || 'exam'}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
  const fileName = `examination-${safeName}-${Date.now()}.pdf`
  doc.save(fileName)
  return { success: true, fileName }
}

/**
 * Export examination snapshot with optional section/question overrides.
 */
export const exportExaminationToPDF = async (
  examination,
  client,
  medicalHistory = null,
  options = {}
) => {
  try {
    const doc = buildExaminationPdf({
      examination: examination || {},
      client: client || {},
      medicalHistory: medicalHistory || {},
      options: {
        sections: options.sections || DEFAULT_PDF_SECTIONS,
        blankQuestionnaireSlots: options.blankQuestionnaireSlots ?? 4,
        title: options.title || 'NaturAssist Examination Record',
      },
    })
    return saveDoc(doc, client)
  } catch (error) {
    console.error('Error exporting examination to PDF:', error)
    throw error
  }
}

export const formValuesToExamSnapshot = (formValues = {}, customQuestions = []) => {
  const client = {
    first_name: formValues.firstName || '',
    last_name: formValues.lastName || '',
    date_of_birth: formValues.dateOfBirth || '',
    gender: formValues.gender || '',
    phone: formValues.phone || '',
    email: formValues.email || '',
  }

  const medicalHistory = {
    current_medications: formValues.currentMedications,
    allergies: formValues.allergies,
    past_surgeries: formValues.pastSurgeries,
    family_history: formValues.familyHistory,
    chronic_conditions: formValues.chronicConditions,
    diet: formValues.diet,
    exercise: formValues.exercise,
    sleep: formValues.sleep,
    custom_habits: formValues.customHabits,
    stress_level: formValues.stressLevel,
    alcohol: formValues.alcohol,
    smoking: formValues.smoking,
    primary_goals: formValues.primaryGoals,
    expectations: formValues.expectations,
    previous_treatments: formValues.previousTreatments,
  }

  const examination = {
    created_at: formValues.examinationDate || new Date().toISOString(),
    examination_date: formValues.examinationDate || '',
    client_consent: formValues.clientConsent ? 'Yes' : '',
    consent_date: formValues.consentDate || '',
    consent_witness: formValues.consentWitness || '',
    notes: formValues.notes || '',
    vital_signs: {
      bloodPressure: formValues.bloodPressure,
      heartRate: formValues.heartRate,
      respiratoryRate: formValues.respiratoryRate,
      temperature: formValues.temperature,
      oxygenSaturation: formValues.oxygenSaturation,
      weight: formValues.weight,
      height: formValues.height,
      bmi: formValues.bmi,
    },
    custom_questions: (customQuestions || [])
      .filter((q) => q?.prompt || q?.answer)
      .map((q) => ({
        prompt: q.prompt || '',
        type: q.type || 'text',
        answer: q.answer || '',
      })),
  }

  return { client, medicalHistory, examination }
}

/**
 * Export current form values as PDF (supports section options).
 */
export const exportExaminationFormToPDF = async (
  formValues = {},
  customQuestions = [],
  options = {}
) => {
  const snapshot = formValuesToExamSnapshot(formValues, customQuestions)
  return exportExaminationToPDF(
    snapshot.examination,
    snapshot.client,
    snapshot.medicalHistory,
    options
  )
}

/**
 * Blank printable chart.
 */
export const exportBlankExaminationFormToPDF = async (options = {}) => {
  try {
    const doc = buildExaminationPdf({
      examination: {},
      client: {},
      medicalHistory: {},
      options: {
        title: options.title || 'NaturAssist Examination Form',
        sections: options.sections || DEFAULT_PDF_SECTIONS,
        blankQuestionnaireSlots: options.blankQuestionnaireSlots ?? 8,
      },
    })
    const fileName = `examination-blank-form-${Date.now()}.pdf`
    doc.save(fileName)
    return { success: true, fileName }
  } catch (error) {
    console.error('Error exporting blank examination form:', error)
    throw error
  }
}

/**
 * Export examination comparison to PDF
 */
export const exportExaminationComparisonToPDF = async (examination1, examination2, client) => {
  try {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20
    const colWidth = (pageWidth - 2 * margin) / 2
    let yPos = margin

    const checkPageBreak = (requiredSpace = 20) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage()
        yPos = margin
        return true
      }
      return false
    }

    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('Examination Comparison', pageWidth / 2, yPos, { align: 'center' })
    yPos += 10

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(
      `Comparison: ${formatDate(examination1.created_at)} vs ${formatDate(examination2.created_at)}`,
      pageWidth / 2,
      yPos,
      { align: 'center' }
    )
    yPos += 15

    if (client) {
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Client Information', margin, yPos)
      yPos += 8
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Name: ${client.first_name} ${client.last_name}`, margin, yPos)
      yPos += 11
    }

    checkPageBreak(40)

    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(formatDate(examination1.created_at), margin, yPos)
    doc.text(formatDate(examination2.created_at), margin + colWidth, yPos)
    yPos += 10

    if (examination1.vital_signs || examination2.vital_signs) {
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Vital Signs', margin, yPos)
      yPos += 8

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      ;[
        { key: 'bloodPressure', label: 'BP' },
        { key: 'heartRate', label: 'HR' },
        { key: 'respiratoryRate', label: 'RR' },
        { key: 'temperature', label: 'Temp' },
        { key: 'oxygenSaturation', label: 'SpO₂' },
        { key: 'weight', label: 'Weight' },
        { key: 'height', label: 'Height' },
        { key: 'bmi', label: 'BMI' },
      ].forEach((field) => {
        checkPageBreak(15)
        const val1 = examination1.vital_signs?.[field.key] || 'N/A'
        const val2 = examination2.vital_signs?.[field.key] || 'N/A'
        const changed = val1 !== val2
        doc.setTextColor(changed ? 220 : 0, changed ? 38 : 0, changed ? 38 : 0)
        doc.text(`${field.label}:`, margin, yPos)
        doc.text(String(val1), margin + 30, yPos)
        doc.text(String(val2), margin + colWidth + 30, yPos)
        yPos += 6
      })
      doc.setTextColor(0, 0, 0)
    }

    const totalPages = doc.internal.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
    }

    const fileName = `examination-comparison-${examination1.id}-${examination2.id}-${Date.now()}.pdf`
    doc.save(fileName)
    return { success: true, fileName }
  } catch (error) {
    console.error('Error exporting examination comparison to PDF:', error)
    throw error
  }
}
